"use client";

import { c as _c } from "react/compiler-runtime";
import { IconXmarkOutline18 } from "nucleo-ui-outline-18";
import { Command, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { useDebounce } from "@/hooks/useDebounce";
import { useEffect, useMemo, useRef, useState } from "react";
const FALLBACK_COLORS = ["bg-blue-500", "bg-emerald-500", "bg-violet-500", "bg-amber-500", "bg-rose-500", "bg-cyan-500", "bg-indigo-500", "bg-orange-500"] as const;
function tickerColor(symbol: string) {
  let hash = 0;
  for (let i = 0; i < symbol.length; i++) hash = hash * 31 + symbol.charCodeAt(i) | 0;
  return FALLBACK_COLORS[Math.abs(hash) % FALLBACK_COLORS.length];
}
const failedLogos = new Set<string>();
function TickerBadge(t0) {
  const $ = _c(14);
  const {
    symbol,
    name,
    size: t1
  } = t0;
  const size = t1 === undefined ? "sm" : t1;
  const t2 = name || symbol;
  let t3;
  if ($[0] !== t2) {
    t3 = t2.toLowerCase().replace(/[^a-z0-9]/g, "_");
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const logoName = t3;
  let t4;
  if ($[2] !== logoName) {
    t4 = () => failedLogos.has(logoName);
    $[2] = logoName;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const [imgError, setImgError] = useState(t4);
  const dim = size === "sm" ? "h-5 w-5 text-[10px]" : "h-6 w-6 text-xs";
  if (imgError) {
    const t5 = `${tickerColor(symbol)} ${dim} inline-flex shrink-0 items-center justify-center rounded font-semibold text-white`;
    let t6;
    if ($[4] !== symbol[0] || $[5] !== t5) {
      t6 = <span className={t5}>{symbol[0]}</span>;
      $[4] = symbol[0];
      $[5] = t5;
      $[6] = t6;
    } else {
      t6 = $[6];
    }
    return t6;
  }
  const t5 = `https://ai-icon.b3.fun/logo/${logoName}`;
  let t6;
  if ($[7] !== logoName) {
    t6 = () => {
      failedLogos.add(logoName);
      setImgError(true);
    };
    $[7] = logoName;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = `${dim} shrink-0 rounded object-cover`;
  let t8;
  if ($[9] !== symbol || $[10] !== t5 || $[11] !== t6 || $[12] !== t7) {
    t8 = <img src={t5} alt={symbol} onError={t6} className={t7} />;
    $[9] = symbol;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  return t8;
}
import type { TickerSearchResult as TickerResult } from "@/app/api/ticker-search/route";
const POPULAR_TICKERS: TickerResult[] = [{
  symbol: "AAPL",
  name: "Apple",
  exchange: "NASDAQ",
  type: "EQUITY"
}, {
  symbol: "MSFT",
  name: "Microsoft",
  exchange: "NASDAQ",
  type: "EQUITY"
}, {
  symbol: "GOOGL",
  name: "Alphabet",
  exchange: "NASDAQ",
  type: "EQUITY"
}, {
  symbol: "AMZN",
  name: "Amazon",
  exchange: "NASDAQ",
  type: "EQUITY"
}, {
  symbol: "META",
  name: "Meta Platforms",
  exchange: "NASDAQ",
  type: "EQUITY"
}, {
  symbol: "NVDA",
  name: "NVIDIA",
  exchange: "NASDAQ",
  type: "EQUITY"
}, {
  symbol: "TSLA",
  name: "Tesla",
  exchange: "NASDAQ",
  type: "EQUITY"
}, {
  symbol: "SPY",
  name: "S&P 500 ETF",
  exchange: "NYSE",
  type: "ETF"
}, {
  symbol: "QQQ",
  name: "Nasdaq 100 ETF",
  exchange: "NASDAQ",
  type: "ETF"
}, {
  symbol: "COIN",
  name: "Coinbase",
  exchange: "NASDAQ",
  type: "EQUITY"
}, {
  symbol: "AMD",
  name: "AMD",
  exchange: "NASDAQ",
  type: "EQUITY"
}, {
  symbol: "JPM",
  name: "JPMorgan Chase",
  exchange: "NYSE",
  type: "EQUITY"
}];
interface TickerSelectMultiProps {
  multiple?: true;
  value: string[];
  onChange: (value: string[]) => void;
  label?: string;
  description?: string;
  placeholder?: string;
}
interface TickerSelectSingleProps {
  multiple: false;
  value: string;
  onChange: (value: string) => void;
  label?: string;
  description?: string;
  placeholder?: string;
}
type TickerSelectProps = TickerSelectMultiProps | TickerSelectSingleProps;
export function TickerSelect(props) {
  const $ = _c(72);
  const {
    label,
    description,
    placeholder: t0
  } = props;
  const placeholder = t0 === undefined ? "Search tickers (e.g. AAPL, SPY)" : t0;
  const isMulti = props.multiple !== false;
  let t1;
  if ($[0] !== isMulti || $[1] !== props.value) {
    t1 = isMulti ? Array.isArray(props.value) ? props.value : [] : props.value ? [props.value] : [];
    $[0] = isMulti;
    $[1] = props.value;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const safeValue = t1;
  let t2;
  if ($[3] !== isMulti || $[4] !== props) {
    t2 = next => {
      if (isMulti) {
        (props as TickerSelectMultiProps).onChange(next);
      } else {
        (props as TickerSelectSingleProps).onChange(next[0] ?? "");
      }
    };
    $[3] = isMulti;
    $[4] = props;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const emitChange = t2;
  const [inputValue, setInputValue] = useState("");
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [apiResults, setApiResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const abortRef = useRef(null);
  let t3;
  if ($[6] !== inputValue) {
    t3 = inputValue.trim();
    $[6] = inputValue;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const debouncedQuery = useDebounce(t3, 300);
  let t4;
  let t5;
  if ($[8] !== debouncedQuery) {
    t4 = () => {
      abortRef.current?.abort();
      if (!debouncedQuery) {
        setApiResults(null);
        setIsSearching(false);
        return;
      }
      const controller = new AbortController();
      abortRef.current = controller;
      setIsSearching(true);
      fetch(`/api/ticker-search?q=${encodeURIComponent(debouncedQuery)}`, {
        signal: controller.signal
      }).then(_temp).then(results => {
        setApiResults(results);
        setIsSearching(false);
        setSelectedIndex(0);
      }).catch(err => {
        if (err instanceof DOMException && err.name === "AbortError") {
          return;
        }
        setApiResults(null);
        setIsSearching(false);
      });
      return () => controller.abort();
    };
    t5 = [debouncedQuery];
    $[8] = debouncedQuery;
    $[9] = t4;
    $[10] = t5;
  } else {
    t4 = $[9];
    t5 = $[10];
  }
  useEffect(t4, t5);
  let t6;
  bb0: {
    let t7;
    if ($[11] !== safeValue) {
      t7 = new Set(safeValue.map(_temp2));
      $[11] = safeValue;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    const alreadySelected = t7;
    if (apiResults !== null) {
      let t8;
      if ($[13] !== alreadySelected || $[14] !== apiResults) {
        let t9;
        if ($[16] !== alreadySelected) {
          t9 = t => !alreadySelected.has(t.symbol.toUpperCase());
          $[16] = alreadySelected;
          $[17] = t9;
        } else {
          t9 = $[17];
        }
        t8 = apiResults.filter(t9);
        $[13] = alreadySelected;
        $[14] = apiResults;
        $[15] = t8;
      } else {
        t8 = $[15];
      }
      t6 = t8;
      break bb0;
    }
    let t8;
    if ($[18] !== alreadySelected || $[19] !== inputValue) {
      const query = inputValue.trim().toUpperCase();
      let t9;
      if ($[22] !== alreadySelected) {
        t9 = t_0 => !alreadySelected.has(t_0.symbol);
        $[22] = alreadySelected;
        $[23] = t9;
      } else {
        t9 = $[23];
      }
      const base = POPULAR_TICKERS.filter(t9);
      if (!query) {
        t6 = base;
        break bb0;
      }
      t8 = base.filter(t_1 => t_1.symbol.includes(query) || t_1.name.toUpperCase().includes(query));
      $[18] = alreadySelected;
      $[19] = inputValue;
      $[20] = t8;
      $[21] = t6;
    } else {
      t8 = $[20];
      t6 = $[21];
    }
    t6 = t8;
  }
  const candidates = t6;
  let t7;
  if ($[24] !== emitChange || $[25] !== isMulti || $[26] !== safeValue) {
    t7 = symbol => {
      if (!safeValue.includes(symbol)) {
        emitChange(isMulti ? [...safeValue, symbol] : [symbol]);
      }
      setInputValue("");
      setApiResults(null);
      setDropdownVisible(false);
    };
    $[24] = emitChange;
    $[25] = isMulti;
    $[26] = safeValue;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  const handleSelect = t7;
  let t8;
  if ($[28] !== emitChange || $[29] !== safeValue) {
    t8 = index => {
      emitChange(safeValue.filter((_, i) => i !== index));
    };
    $[28] = emitChange;
    $[29] = safeValue;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  const handleRemove = t8;
  let t9;
  if ($[31] !== emitChange || $[32] !== inputValue || $[33] !== isMulti || $[34] !== safeValue) {
    t9 = () => {
      const trimmed = inputValue.trim().toUpperCase();
      if (trimmed && !safeValue.includes(trimmed)) {
        emitChange(isMulti ? [...safeValue, trimmed] : [trimmed]);
        setInputValue("");
        setApiResults(null);
        setDropdownVisible(false);
      }
    };
    $[31] = emitChange;
    $[32] = inputValue;
    $[33] = isMulti;
    $[34] = safeValue;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  const handleAdd = t9;
  let t10;
  if ($[36] !== candidates || $[37] !== dropdownVisible || $[38] !== handleAdd || $[39] !== handleSelect || $[40] !== selectedIndex) {
    t10 = e => {
      if (dropdownVisible && candidates.length > 0) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setSelectedIndex(prev => (prev + 1) % candidates.length);
          return;
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          setSelectedIndex(prev_0 => (prev_0 - 1 + candidates.length) % candidates.length);
          return;
        }
        if (e.key === "Enter") {
          e.preventDefault();
          const selected = candidates[selectedIndex];
          if (selected) {
            handleSelect(selected.symbol);
          }
          return;
        }
        if (e.key === "Escape") {
          e.preventDefault();
          setDropdownVisible(false);
          return;
        }
      }
      if (e.key === "Enter") {
        e.preventDefault();
        handleAdd();
      }
    };
    $[36] = candidates;
    $[37] = dropdownVisible;
    $[38] = handleAdd;
    $[39] = handleSelect;
    $[40] = selectedIndex;
    $[41] = t10;
  } else {
    t10 = $[41];
  }
  const handleKeyDown = t10;
  let t11;
  if ($[42] !== label) {
    t11 = label && <label className="field-label block">{label}</label>;
    $[42] = label;
    $[43] = t11;
  } else {
    t11 = $[43];
  }
  let t12;
  if ($[44] !== description) {
    t12 = description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>;
    $[44] = description;
    $[45] = t12;
  } else {
    t12 = $[45];
  }
  let t13;
  if ($[46] !== handleRemove || $[47] !== safeValue) {
    t13 = safeValue.length > 0 && <div className="flex flex-wrap gap-2">{safeValue.map((ticker, index_0) => <div key={ticker} className="flex items-center gap-1.5 rounded-md bg-blue-100 px-2 py-1 text-sm text-blue-700 dark:bg-blue-600/20 dark:text-blue-400"><TickerBadge symbol={ticker} size="sm" /><span className="font-medium">{ticker}</span><button type="button" onClick={() => handleRemove(index_0)} className="rounded hover:bg-blue-200 dark:hover:bg-blue-600/30"><IconXmarkOutline18 className="h-3 w-3" /></button></div>)}</div>;
    $[46] = handleRemove;
    $[47] = safeValue;
    $[48] = t13;
  } else {
    t13 = $[48];
  }
  const t14 = dropdownVisible && candidates.length > 0;
  const t15 = dropdownVisible && candidates[selectedIndex] ? `ticker-option-${candidates[selectedIndex].symbol}` : undefined;
  let t16;
  let t17;
  let t18;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = e_0 => {
      setInputValue(e_0.target.value);
      setDropdownVisible(true);
      setSelectedIndex(0);
    };
    t17 = () => setDropdownVisible(true);
    t18 = () => setTimeout(() => setDropdownVisible(false), 200);
    $[49] = t16;
    $[50] = t17;
    $[51] = t18;
  } else {
    t16 = $[49];
    t17 = $[50];
    t18 = $[51];
  }
  let t19;
  if ($[52] !== handleKeyDown || $[53] !== inputValue || $[54] !== placeholder || $[55] !== t14 || $[56] !== t15) {
    t19 = <Input type="text" role="combobox" aria-expanded={t14} aria-autocomplete="list" aria-activedescendant={t15} value={inputValue} onChange={t16} onFocus={t17} onBlur={t18} onKeyDown={handleKeyDown} placeholder={placeholder} className="text-ds-text-primary" />;
    $[52] = handleKeyDown;
    $[53] = inputValue;
    $[54] = placeholder;
    $[55] = t14;
    $[56] = t15;
    $[57] = t19;
  } else {
    t19 = $[57];
  }
  let t20;
  if ($[58] !== candidates || $[59] !== dropdownVisible || $[60] !== handleSelect || $[61] !== isSearching || $[62] !== selectedIndex) {
    t20 = dropdownVisible && (candidates.length > 0 || isSearching) && <div onMouseDown={_temp3} className="absolute left-0 top-full z-50 mt-1 w-full"><Command shouldFilter={false} className="border-ds-border-default bg-ds-surface-card max-h-[280px] overflow-auto rounded-lg border shadow-xl"><CommandList role="listbox">{isSearching && candidates.length === 0 && <div className="text-ds-text-subtle px-3 py-3 text-center text-xs">Searching...</div>}<CommandGroup>{candidates.map((t_2, i_0) => <CommandItem key={t_2.symbol} id={`ticker-option-${t_2.symbol}`} value={t_2.symbol} role="option" aria-selected={i_0 === selectedIndex} onSelect={() => handleSelect(t_2.symbol)} className={`cursor-pointer px-3 py-1.5 ${i_0 === selectedIndex ? "dark:!bg-ds-neutral-700 !bg-blue-50" : ""}`}><div className="flex items-center gap-2"><TickerBadge symbol={t_2.symbol} name={t_2.name} size="md" /><span className="font-mono text-sm font-semibold">{t_2.symbol}</span><span className="text-ds-text-subtle truncate text-xs">{t_2.name}</span><span className="text-ds-text-disabled ml-auto shrink-0 text-[10px]">{t_2.exchange}</span></div></CommandItem>)}</CommandGroup></CommandList></Command></div>;
    $[58] = candidates;
    $[59] = dropdownVisible;
    $[60] = handleSelect;
    $[61] = isSearching;
    $[62] = selectedIndex;
    $[63] = t20;
  } else {
    t20 = $[63];
  }
  let t21;
  if ($[64] !== t19 || $[65] !== t20) {
    t21 = <div className="relative">{t19}{t20}</div>;
    $[64] = t19;
    $[65] = t20;
    $[66] = t21;
  } else {
    t21 = $[66];
  }
  let t22;
  if ($[67] !== t11 || $[68] !== t12 || $[69] !== t13 || $[70] !== t21) {
    t22 = <div className="space-y-2">{t11}{t12}{t13}{t21}</div>;
    $[67] = t11;
    $[68] = t12;
    $[69] = t13;
    $[70] = t21;
    $[71] = t22;
  } else {
    t22 = $[71];
  }
  return t22;
}
function _temp3(e_1) {
  e_1.preventDefault();
  e_1.stopPropagation();
}
function _temp2(v) {
  return v.toUpperCase();
}
function _temp(r) {
  return r.json();
}